import { invokeCheckout } from "@/lib/checkoutRouter";
import { trackBeginCheckout, trackEvent } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";

export type MentoringInterval = "month" | "year";

/**
 * Single source of truth for buying / upgrading mentoring ("Apoyo Experto").
 *
 * Business rule (applies everywhere: chat, dashboard, billing, sales blocks):
 *  - The user already has an active Club Pro (paid, promo, manual/courtesy or
 *    admin) → they only pay the proportional mentoring price (mentor_addon:
 *    280€ / 480€ / 800€ per month).
 *  - The user has no Panel → they pay the public combined price
 *    (vip_combined: 379€ / 579€ / 899€, Club Pro included).
 *
 * When the user has Panel access but no Stripe subscription to attach the item
 * to (manual/courtesy/admin Panel), `add-mentoring-to-subscription` answers
 * `no_subscription` and we transparently fall back to a regular checkout for
 * the mentor add-on — no error shown to the user.
 */
export async function startMentoringCheckout(opts: {
  hasPanel: boolean;
  tierKey: string;
  interval?: MentoringInterval;
  /** Bundle the Panel as a one-time item (combined purchase edge case). */
  includePanel?: boolean;
}): Promise<{ url?: string | undefined; success?: boolean | undefined; error?: string | undefined }> {
  const interval: MentoringInterval = opts.interval === "year" ? "year" : "month";

  const addonCheckout = async () => {
    const { data, error } = await invokeCheckout( {
      body: {
        product_type: "vip_plan", // mapped to mentor_addon pricing server-side
        product_key: opts.tierKey,
        billing_interval: interval,
        ...(opts.includePanel ? { include_panel: true } : {}),
      },
    });
    if (error) throw error;
    if (data?.error) return { error: data.error as string };
    if (data?.url) trackBeginCheckout({ source: "mentoria", productType: "vip_plan", productKey: opts.tierKey, interval });
    return { url: data?.url as string | undefined, success: !!data?.success };
  };

  if (opts.hasPanel) {
    const { data, error } = await supabase.functions.invoke(
      "add-mentoring-to-subscription",
      { body: { tier: opts.tierKey, billing_interval: interval } },
    );
    if (error) throw error;
    // No Stripe subscription (manual / courtesy / admin Panel) → proportional checkout
    if (data?.error === "no_subscription") return await addonCheckout();
    if (data?.error) return { error: data.error as string };
    if (data?.url) trackBeginCheckout({ source: "mentoria", productType: "vip_plan", productKey: opts.tierKey, interval });
    else if (data?.success) trackEvent("subscription_upgrade", { product_type: "vip_plan", product_key: opts.tierKey, billing_interval: interval });
    return { url: data?.url as string | undefined, success: !!data?.success };
  }

  const { data, error } = await invokeCheckout( {
    body: {
      product_type: "vip_combined",
      product_key: opts.tierKey,
      billing_interval: interval,
    },
  });
  if (error) throw error;
  if (data?.error) return { error: data.error as string };
  if (data?.url) trackBeginCheckout({ source: "mentoria", productType: "vip_combined", productKey: opts.tierKey, interval });
  return { url: data?.url as string | undefined, success: !!data?.success };
}

/** Cents actually charged for a tier, given the user's Panel status. */
export function mentoringChargeCents(args: {
  hasPanel: boolean;
  mentorCents: number;
  combinedCents: number;
}): number {
  return args.hasPanel ? args.mentorCents : args.combinedCents;
}
